import * as React from 'react';
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from 'react-share';

import { useSiteMetadata } from '../../utils/useSiteMetadata';
import { Button } from '../Button/Button';
import { ButtonListSection } from '../ButtonListSection/ButtonListSection';
import { getLinkHost, getLinkPath } from '../../utils/link';
import { track } from '../../utils/analytics';
import { toKebabCase } from '../../utils/string';

import * as cs from './ShareButtons.module.css';

interface Props {
  header?: string;
  title?: string;
  description?: string;
  tags: CustomTypes.Tags;
  authors?: readonly Queries.authorFieldsFragment[];
  opensourceProjects?: Queries.opensourceProjectFancyListFieldsFragment[];
}

type ButtonType =
  | typeof FacebookShareButton
  | typeof LinkedinShareButton
  | typeof TwitterShareButton;

interface ShareEntry {
  label: string;
  Component: ButtonType;
  props: React.ComponentProps<ButtonType>;
}

export const ShareButtons = ({
  header = 'Share on',
  title = '',
  description = '',
  tags = [],
  authors = [],
  opensourceProjects = [],
}: Props) => {
  const siteMetadata = useSiteMetadata();

  const formattedTags = tags
    .slice(0, 3)
    .map((tag) =>
      (tag.shortLabel || tag.label).replace(/\s|\./g, '').replace(/&/g, 'And'),
    );

  const opensourceTwitters = opensourceProjects.reduce(
    (
      acc: string[],
      project: Queries.opensourceProjectFancyListFieldsFragment,
    ) => {
      if (!project.linksExtra || project.linksExtra.length < 1) {
        return acc;
      }

      const twitterLink = project.linksExtra.find(
        (link) => getLinkHost(link).split('.')[0] === 'twitter',
      );

      if (twitterLink) {
        return [...acc, getLinkPath(twitterLink)];
      }

      return acc;
    },
    [],
  );

  const authorsTwitters = authors
    .filter((author) => Boolean(author.twitter))
    .map((author) => author.twitter?.replace('@', '') || '');

  const url = typeof window !== 'undefined' ? window.location.href : '';

  const renderShare = (label: string) => (
    <Button
      size="sm"
      variant="translucent"
      theme="current_text"
      type="tag"
      hoverable
    >
      {label}
    </Button>
  );

  const shareButtons: ShareEntry[] = [
    {
      label: 'Twitter',
      Component: TwitterShareButton,
      props: {
        title,
        via: 'evilmartians',
        hashtags: formattedTags,
        related: ['evilmartians', ...opensourceTwitters, ...authorsTwitters],
        url,
        children: renderShare('Twitter'),
      },
    },
    {
      label: 'Facebook',
      Component: FacebookShareButton,
      props: {
        quote: description,
        hashtag: `#${formattedTags[0]}`,
        url,
        children: renderShare('Facebook'),
      },
    },
    {
      label: 'LinkedIn',
      Component: LinkedinShareButton,
      props: {
        title,
        summary: description,
        source: siteMetadata.siteUrl,
        url,
        children: renderShare('LinkedIn'),
      },
    },
  ];

  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : '';

  const renderButton = (entry: ShareEntry) => (
    <entry.Component
      resetButtonStyle
      onClick={() => {
        track(`share-page-${toKebabCase(entry.label)}`, pathname);
      }}
      className={cs.buttonWrapper}
      {...entry.props}
    />
  );

  return (
    <ButtonListSection<ShareEntry>
      entries={shareButtons}
      title={header}
      renderButton={renderButton}
    />
  );
};
